import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, ShieldOff, AlertCircle, Copy, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export function StatusBanner({ report }) {
  const { scan_status, warnings, recommendation } = report;
  const navigate = useNavigate();

  if (scan_status === 'complete') return null;

  const configs = {
    partial_network: {
      icon: <ShieldAlert className="w-6 h-6 text-amber-600" />,
      title: 'Partial scan -- site protection detected',
      bgClass: 'bg-amber-50 border-amber-200',
      textClass: 'text-amber-900',
    },
    partial_static: {
      icon: <ShieldAlert className="w-6 h-6 text-orange-600" />,
      title: 'Limited scan -- static signals only',
      bgClass: 'bg-orange-50 border-orange-200',
      textClass: 'text-orange-900',
    },
    blocked: {
      icon: <ShieldOff className="w-6 h-6 text-red-600" />,
      title: 'Scan blocked by site protection',
      bgClass: 'bg-red-50 border-red-200',
      textClass: 'text-red-900',
    },
    error: {
      icon: <AlertCircle className="w-6 h-6 text-red-600" />,
      title: 'Scan error',
      bgClass: 'bg-red-50 border-red-200',
      textClass: 'text-red-900',
    },
  };

  const config = configs[scan_status] || configs.error;

  return (
    <div className={`rounded-2xl border p-6 mb-6 ${config.bgClass}`} data-testid="status-banner">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5">{config.icon}</div>
        <div className="flex-1">
          <h3 className={`text-lg font-semibold ${config.textClass}`} style={{ fontFamily: 'Manrope' }}>
            {config.title}
          </h3>
          {warnings?.[0] && <p className="text-sm mt-1 opacity-80">{warnings[0]}</p>}
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            {recommendation?.primary_action && (
              <Button
                className="bg-[#007AFF] hover:bg-[#0062CC] rounded-full text-sm"
                onClick={() => {
                  const href = recommendation.primary_action.href;
                  if (href === '#results') {
                    document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
                  } else if (href === '#retry') {
                    navigate('/');
                  } else {
                    navigate(href);
                  }
                }}
                data-testid="banner-primary-action"
              >
                {recommendation.primary_action.label}
              </Button>
            )}
            {recommendation?.secondary_action && (
              <Button
                variant="outline"
                className="rounded-full text-sm"
                onClick={() => navigate(recommendation.secondary_action.href)}
                data-testid="banner-secondary-action"
              >
                {recommendation.secondary_action.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TechnicalDetails({ error }) {
  const [open, setOpen] = useState(false);
  if (!error) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden" data-testid="technical-details">
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-slate-400" />
          <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Technical details</span>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 pt-4">
          <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm space-y-2">
            <div className="text-slate-400"><span className="text-slate-500">error_type:</span> {error.error_type}</div>
            <div className="text-slate-400"><span className="text-slate-500">error_code:</span> {error.error_code}</div>
            <div className="text-amber-400"><span className="text-slate-500">message:</span> {error.message}</div>
            {error.technical && (
              <div className="text-slate-500 text-xs break-all"><span className="text-slate-600">technical:</span> {error.technical}</div>
            )}
          </div>
          <Button
            variant="outline" size="sm" className="mt-3 gap-2"
            onClick={() => {
              const info = `error_type: ${error.error_type}\nerror_code: ${error.error_code}\nmessage: ${error.message}\ntechnical: ${error.technical}`;
              navigator.clipboard.writeText(info);
              toast.success('Diagnostic info copied');
            }}
            data-testid="copy-diagnostic-btn"
          >
            <Copy className="w-3 h-3" /> Copy diagnostic info
          </Button>
        </div>
      )}
    </div>
  );
}

export function ConfidenceBadge({ confidence, isPartial }) {
  if (!confidence || confidence === 'none') return null;
  const colors = {
    high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors[confidence] || ''}`} data-testid="confidence-badge">
      {isPartial ? 'Partial' : confidence} confidence
    </span>
  );
}
